/**
 * @file Utility: buttonSoundManager
 * @description Utility for adding sound effects to all button elements in the application
 */

import { useEffect } from 'react';
import { useAudioContext } from '../contexts/AudioContext';
import { useSoundEffect } from '../hooks/useSoundEffects';

/**
 * Hook to add click sound effects to all button elements in the application
 * This should be used in a high-level component like App.tsx
 */
export function useGlobalButtonSounds(): void {
  const { isSfxEnabled } = useAudioContext();
  const { play: playButtonSound } = useSoundEffect('BUTTON_PRESS');

  useEffect(() => {
    if (!isSfxEnabled) return;

    // Function to handle button clicks
    const handleButtonClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if the clicked element is a button or has a role of button
      const isButton =
        target.tagName.toLowerCase() === 'button' ||
        target.getAttribute('role') === 'button' ||
        target.closest('button') !== null;

      // Check if the button is disabled
      const isDisabled =
        target.hasAttribute('disabled') ||
        target.classList.contains('disabled') ||
        target.getAttribute('aria-disabled') === 'true' ||
        (target.closest('button')?.hasAttribute('disabled') ?? false);

      // Only play sound for enabled buttons
      if (isButton && !isDisabled) {
        // Don't play sound for buttons that already have the Button component's sound
        // We can check for a data attribute that the Button component could set
        const hasCustomSound =
          target.hasAttribute('data-has-sound') ||
          (target.closest('button')?.hasAttribute('data-has-sound') ?? false);

        if (!hasCustomSound) {
          playButtonSound();
        }
      }
    };

    // Add the event listener to the document
    document.addEventListener('click', handleButtonClick, true);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener('click', handleButtonClick, true);
    };
  }, [isSfxEnabled, playButtonSound]);
}

/**
 * Function to initialize the button sound manager
 * This should be called once at application startup
 */
export function initButtonSoundManager(): void {
  // Add a data attribute to the Button component to prevent double sounds
  const addDataAttributeToButtons = () => {
    // Find all buttons that use the Button component
    // This is a heuristic and might need adjustment based on your actual Button component implementation
    const buttons = document.querySelectorAll(
      'button.font-bold.rounded-none.transition-all.duration-300.uppercase.tracking-widest.shadow-md',
    );

    buttons.forEach((button) => {
      button.setAttribute('data-has-sound', 'true');
    });
  };

  // Run this function when the DOM is fully loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', addDataAttributeToButtons);
  } else {
    addDataAttributeToButtons();
  }

  // Also run it after any route changes or major DOM updates
  // This is a simplified approach and might need to be adjusted based on your routing solution
  const observer = new MutationObserver(() => {
    addDataAttributeToButtons();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}
